import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Lock, FileText, Receipt, Palette, Building2, Image as ImageIcon, Webhook, Sparkles, Truck, FileUp, Bell } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import ProductionConfigSection from "./ProductionConfigSection";
import LetterheadsSection from "./LetterheadsSection";
import InvoiceDisclaimerSection from "./InvoiceDisclaimerSection";
import BrandingSection from "./BrandingSection";
import CompanyContactSection from "./CompanyContactSection";
import DocumentLogoSection from "./DocumentLogoSection";
import ResendWebhookSection from "./ResendWebhookSection";
import TrackingPresetsSection from "./TrackingPresetsSection";
import DriversSection from "./DriversSection";
import CustomerImportSection from "./CustomerImportSection";
import ReminderSettingsSection from "./ReminderSettingsSection";
import { PlatformIdentity } from "../../platform/PlatformIdentity";

export default function InstellingenSection() {
  const { user, hasPermission } = useAuth();
  const canViewProd = hasPermission("settings:production_view");
  const canViewLetterheads = ["owner", "admin", "manager"].includes(user?.role);
  const canViewDisclaimer = ["owner", "admin", "manager"].includes(user?.role);
  const canViewBranding = ["owner", "admin", "manager", "content_manager"].includes(user?.role);
  const canViewContact = ["owner", "admin", "manager", "content_manager"].includes(user?.role);
  const canViewDocLogo = ["owner", "admin", "manager", "medewerker"].includes(user?.role);
  const canViewIntegrations = ["owner", "admin"].includes(user?.role);
  const canViewTrackingPresets = ["owner", "admin", "manager"].includes(user?.role);
  const canViewDrivers = hasPermission("drivers:view");
  // P1 — Klantenimport: alleen voor wie klanten mag aanmaken (Owner/Admin/Manager).
  const canViewCustomerImport = hasPermission("customers:create");
  // Sprint 5B — Herinneringen: owner/admin/manager kan lezen; wijzigen alleen owner/admin.
  const canViewReminders = ["owner", "admin", "manager"].includes(user?.role);
  const [tab, setTab] = useState(
    canViewContact
      ? "bedrijfsgegevens"
      : canViewProd
      ? "productie"
      : canViewBranding
      ? "branding"
      : canViewDocLogo
      ? "documentlogo"
      : canViewLetterheads
      ? "briefpapier"
      : canViewDisclaimer
      ? "factuurdisclaimer"
      : "algemeen",
  );

  return (
    <div className="space-y-4" data-testid="instellingen-section">
      <Tabs value={tab} onValueChange={setTab}>
        {/* Mobile: horizontaal scrollbare tabs zodat ook 'Chauffeurs' en
            'Trackingpresets' bereikbaar blijven op iPhone. Op desktop
            identiek aan voorheen. */}
        <div className="-mx-2 sm:mx-0 overflow-x-auto scrollbar-none">
        <TabsList className="rounded-none flex-nowrap whitespace-nowrap inline-flex w-max sm:w-auto px-2 sm:px-0">
          {canViewContact && (
            <TabsTrigger value="bedrijfsgegevens" data-testid="instellingen-tab-bedrijfsgegevens">
              <Building2 className="w-4 h-4 mr-2" /> Bedrijfsgegevens
            </TabsTrigger>
          )}
          {canViewProd && (
            <TabsTrigger value="productie" data-testid="instellingen-tab-productie">
              <Lock className="w-4 h-4 mr-2" /> Productieconfiguratie
            </TabsTrigger>
          )}
          {canViewBranding && (
            <TabsTrigger value="branding" data-testid="instellingen-tab-branding">
              <Palette className="w-4 h-4 mr-2" /> Branding
            </TabsTrigger>
          )}
          {canViewDocLogo && (
            <TabsTrigger value="documentlogo" data-testid="instellingen-tab-documentlogo">
              <ImageIcon className="w-4 h-4 mr-2" /> Documentlogo
            </TabsTrigger>
          )}
          {canViewLetterheads && (
            <TabsTrigger value="briefpapier" data-testid="instellingen-tab-briefpapier">
              <FileText className="w-4 h-4 mr-2" /> Briefpapier
            </TabsTrigger>
          )}
          {canViewDisclaimer && (
            <TabsTrigger value="factuurdisclaimer" data-testid="instellingen-tab-factuurdisclaimer">
              <Receipt className="w-4 h-4 mr-2" /> Factuurdisclaimer
            </TabsTrigger>
          )}
          {canViewIntegrations && (
            <TabsTrigger value="integraties" data-testid="instellingen-tab-integraties">
              <Webhook className="w-4 h-4 mr-2" /> Integraties
            </TabsTrigger>
          )}
          {canViewTrackingPresets && (
            <TabsTrigger value="trackingpresets" data-testid="instellingen-tab-trackingpresets" className="whitespace-nowrap">
              <Sparkles className="w-4 h-4 mr-2" /> Trackingpresets
            </TabsTrigger>
          )}
          {canViewDrivers && (
            <TabsTrigger value="chauffeurs" data-testid="instellingen-tab-chauffeurs" className="whitespace-nowrap">
              <Truck className="w-4 h-4 mr-2" /> Chauffeurs
            </TabsTrigger>
          )}
          {canViewCustomerImport && (
            <TabsTrigger value="klantenimport" data-testid="instellingen-tab-klantenimport" className="whitespace-nowrap">
              <FileUp className="w-4 h-4 mr-2" /> Klantenimport
            </TabsTrigger>
          )}
          {canViewReminders && (
            <TabsTrigger value="herinneringen" data-testid="instellingen-tab-herinneringen" className="whitespace-nowrap">
              <Bell className="w-4 h-4 mr-2" /> Herinneringen
            </TabsTrigger>
          )}
          <TabsTrigger value="algemeen" data-testid="instellingen-tab-algemeen" className="whitespace-nowrap">
            Algemeen
          </TabsTrigger>
        </TabsList>
        </div>

        {canViewContact && (
          <TabsContent value="bedrijfsgegevens" className="mt-4">
            <CompanyContactSection />
          </TabsContent>
        )}

        {canViewProd && (
          <TabsContent value="productie" className="mt-4">
            <ProductionConfigSection />
          </TabsContent>
        )}

        {canViewBranding && (
          <TabsContent value="branding" className="mt-4">
            <BrandingSection />
          </TabsContent>
        )}

        {canViewDocLogo && (
          <TabsContent value="documentlogo" className="mt-4">
            <DocumentLogoSection />
          </TabsContent>
        )}

        {canViewLetterheads && (
          <TabsContent value="briefpapier" className="mt-4">
            <LetterheadsSection />
          </TabsContent>
        )}

        {canViewDisclaimer && (
          <TabsContent value="factuurdisclaimer" className="mt-4">
            <InvoiceDisclaimerSection />
          </TabsContent>
        )}

        {canViewIntegrations && (
          <TabsContent value="integraties" className="mt-4">
            <ResendWebhookSection />
          </TabsContent>
        )}

        {canViewTrackingPresets && (
          <TabsContent value="trackingpresets" className="mt-4">
            <TrackingPresetsSection />
          </TabsContent>
        )}

        {canViewDrivers && (
          <TabsContent value="chauffeurs" className="mt-4">
            <DriversSection />
          </TabsContent>
        )}

        {canViewCustomerImport && (
          <TabsContent value="klantenimport" className="mt-4">
            <CustomerImportSection />
          </TabsContent>
        )}

        {canViewReminders && (
          <TabsContent value="herinneringen" className="mt-4">
            <ReminderSettingsSection />
          </TabsContent>
        )}

        <TabsContent value="algemeen" className="mt-4">
          <PlatformIdentity context="settings" />
          <div className="bg-white border border-gray-200 p-6">
            <h2 className="font-chivo font-bold text-lg text-[#0A192F]">Algemene instellingen</h2>
            <p className="text-sm text-gray-600 mt-2">
              Voor bedrijfsnaam, adres, telefoon en e-mailadressen gebruik je het tabblad{" "}
              <span className="font-chivo font-bold">Bedrijfsgegevens</span>.
              Voor productie-keys (Resend &amp; Stripe) gebruik je het tabblad{" "}
              <span className="font-chivo font-bold">Productieconfiguratie</span>.
              Voor website-logo en favicon gebruik je het tabblad{" "}
              <span className="font-chivo font-bold">Branding</span>.
              Voor het logo op PDF-facturen en documenten gebruik je het tabblad{" "}
              <span className="font-chivo font-bold">Documentlogo</span>.
              Voor PDF-briefpapier gebruik je het tabblad{" "}
              <span className="font-chivo font-bold">Briefpapier</span>.
              De disclaimer-tekst op klantfacturen beheert u onder{" "}
              <span className="font-chivo font-bold">Factuurdisclaimer</span>.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
